/**
 * LoginTips - rotating product tips with visual examples, shown beside the
 * sign-in form. Colors and fonts mirror the real annotation elements
 * (packages/core/src/styles/colors.ts and text.ts).
 */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { colors, text } from '@annote/core/styles';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 3rem;
  text-align: center;
`;

const Visual = styled.div`
  width: 100%;
  max-width: 420px;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
`;

const TipTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-family: 'Baskervville', Georgia, serif;
  font-size: 1.75rem;
  font-weight: 400;
  color: #373530;
`;

const TipBody = styled.p`
  margin: 0 0 2rem;
  max-width: 380px;
  color: #5f5d59;
  line-height: 1.6;
`;

const Dots = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#ffd400' : '#dbdbda')};
  transition: background 0.2s ease;
`;

const FadeIn = styled.div`
  animation: tipFade 0.45s ease;
  display: flex;
  flex-direction: column;
  align-items: center;

  @keyframes tipFade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

/* --- Visual example building blocks, using the app's real style tokens --- */

const SampleText = styled.p`
  margin: 0;
  font-family: ${text.family.reading};
  font-size: 1.125rem;
  line-height: 1.9;
  color: ${colors.text.primary};
  text-align: left;
`;

const Hl = styled.span`
  background: ${colors.bg.quote.primary};
`;

const HlNote = styled.span`
  background: ${colors.bg.note.primary};
  padding-left: 0.35rem;
  font-family: ${text.family.handwriting};
  font-size: ${text.size.handwriting};
`;

/* A real card: square corners, white, the app's exact shadows and font.
   $target shows the blue link-dropzone outline; $dragging shows the lifted
   drag state (Card.tsx CardOutline). */
const AppCard = styled.div<{ $target?: boolean; $dragging?: boolean }>`
  position: relative;
  background: ${colors.bg.primary};
  box-shadow: ${({ $dragging }) => ($dragging ? colors.shadow.card.hover : colors.shadow.card.default)};
  font-family: ${text.family.primary};
  font-size: ${text.size.primary};
  color: ${colors.text.primary};
  line-height: ${text.height.primary};
  padding: 10px 12px;
  width: fit-content;
  max-width: 250px;
  text-align: left;
  ${({ $target }) => $target && `outline: 1px solid ${colors.lines.selected}; box-shadow: ${colors.shadow.card.hover};`}
  ${({ $dragging }) => $dragging && 'transform: rotate(3deg); opacity: 0.9;'}
`;

const WikiLink = styled.span`
  color: ${colors.text.link};
`;

/* The round link/backlink ports from the app's cards (Port.tsx) */
const Port = styled.i<{ $kind: 'link' | 'backlink' }>`
  position: absolute;
  width: 16px;
  height: 16px;
  ${({ $kind }) => ($kind === 'backlink' ? 'right: -1rem; top: 1rem;' : 'left: -1rem; bottom: 1rem;')}
  background-color: ${colors.bg.primary};
  border-radius: 50%;
  border: 1px solid ${colors.lines.link};
  box-shadow: 0px 0.5px 1px 0px rgba(15, 15, 15, 0.08);
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${colors.icon.placeholder};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(255, 212, 0, 0.2);
    color: ${colors.icon.hover};
  }
`;

const Caret = styled.span`
  display: inline-block;
  width: 1.5px;
  height: 1.1em;
  margin-left: 1px;
  background: ${colors.text.primary};
  vertical-align: -0.15em;
  animation: caretBlink 1s steps(1) infinite;

  @keyframes caretBlink {
    0%,
    49% {
      opacity: 1;
    }
    50%,
    100% {
      opacity: 0;
    }
  }
`;

const DragScene = styled.div`
  position: relative;
  padding: 1rem 0 2.5rem;
`;

const DraggedCard = styled(AppCard)`
  position: absolute;
  left: 45%;
  top: 2.4rem;
  z-index: 2;
`;

const DragCursor = styled.span`
  position: absolute;
  left: 78%;
  top: 4.4rem;
  z-index: 3;
  filter: drop-shadow(0 1px 2px rgba(44, 36, 23, 0.35));
`;

const AButton = styled.span`
  display: inline-flex;
  vertical-align: middle;
  margin-left: 0.5rem;

  svg {
    border-radius: 6px;
    box-shadow: ${colors.shadow.card.default};
  }
`;

/* The app's chat / search input chrome (ChatInput + TextInput family) */
const InputBox = styled.div`
  width: 100%;
  background: ${colors.bg.primary};
  border: 1px solid ${colors.lines.link};
  border-radius: 8px;
  padding: 10px 12px 10px 16px;
  font-family: ${text.family.primary};
  font-size: ${text.size.primary};
  color: ${colors.text.placeholder};
  text-align: left;
  box-shadow: ${colors.shadow.toolbar};
`;

const ResultsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  width: 100%;
  align-items: stretch;

  ${AppCard} {
    max-width: none;
    width: auto;
  }
`;

const CursorSvg = (): React.JSX.Element => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path d="M5 3l14 8-6.5 1.5L9 19z" fill="#2c2417" stroke="#fffdf7" strokeWidth="1.5" />
  </svg>
);

const AIcon = (): React.JSX.Element => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="6" fill="#FFEA61" />
    <path
      d="M15.5111 19.9492L14.6003 16.464H9.25705L8.34627 19.9492H3.10015L8.81987 2.42574H15.2804L21.0001 19.9492H15.5111ZM11.8801 6.46962L10.1921 12.833H13.6652L11.9773 6.46962H11.8801Z"
      fill="black"
    />
  </svg>
);

interface Tip {
  title: string;
  body: string;
  visual: React.JSX.Element;
}

export const LOGIN_TIPS: Tip[] = [
  {
    title: 'Press A to annotate',
    body: 'Select text on any page and press the A key (or click the yellow button) to save a highlight without breaking your reading flow.',
    visual: (
      <SampleText>
        Select any sentence that stops you, <Hl>press A, and it is saved to your notes</Hl>
        <AButton>
          <AIcon />
        </AButton>
      </SampleText>
    ),
  },
  {
    title: 'Talk back in the margins',
    body: 'Type right after highlighting to attach a note in your own words. Your reaction is worth more than the quote.',
    visual: (
      <SampleText>
        <Hl>Notes are a conversation with what you read.</Hl>
        <HlNote>&nbsp;yes! argue with the author&nbsp;</HlNote>
      </SampleText>
    ),
  },
  {
    title: 'Link everything with [[',
    body: 'Type [[ in any note to connect it to another card. Links are where insights come from.',
    visual: (
      <AppCard>
        Ideas compound when they connect. This one belongs with <WikiLink>[[how to take smart notes]]</WikiLink>
        <Caret />
      </AppCard>
    ),
  },
  {
    title: 'Drag cards to connect them',
    body: 'Drop one card onto another to link them. Your notes become a web, not a pile.',
    visual: (
      <DragScene>
        <AppCard $target>
          Reading as a two-way conversation
          <Port $kind="backlink" />
        </AppCard>
        <DraggedCard $dragging>
          Marginalia, from Fermat to Twitter
          <Port $kind="link" />
        </DraggedCard>
        <DragCursor>
          <CursorSvg />
        </DragCursor>
      </DragScene>
    ),
  },
  {
    title: 'Ask Anni',
    body: 'Anni can summarize, write, or explain across your notes and articles, and suggest connections you missed.',
    visual: <InputBox>Ask Anni to summarize, write, or explain your notes and articles&hellip;</InputBox>,
  },
  {
    title: 'Search your second brain',
    body: 'Find anything by content, tag, or source. The more you save, the smarter it gets.',
    visual: (
      <ResultsColumn>
        <InputBox>&#128269;&nbsp; zettelkasten</InputBox>
        <AppCard>How to Take Smart Notes &mdash; the slip box, explained</AppCard>
        <AppCard>Luhmann: a note system as a conversation partner</AppCard>
      </ResultsColumn>
    ),
  },
];

const ROTATE_MS = 12000;

const LoginTips: React.FC = () => {
  const [index, setIndex] = useState(0);
  // bumped on manual navigation so the auto-rotate timer starts over
  const [timerEpoch, setTimerEpoch] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % LOGIN_TIPS.length);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [timerEpoch]);

  const goTo = (i: number): void => {
    setIndex((i + LOGIN_TIPS.length) % LOGIN_TIPS.length);
    setTimerEpoch((e) => e + 1);
  };

  const tip = LOGIN_TIPS[index];

  return (
    <Wrapper>
      <FadeIn key={index}>
        <Visual>{tip.visual}</Visual>
        <TipTitle>{tip.title}</TipTitle>
        <TipBody>{tip.body}</TipBody>
      </FadeIn>
      <NavRow>
        <NavButton onClick={() => goTo(index - 1)} aria-label="Previous tip">
          <ChevronLeft size={18} />
        </NavButton>
        <Dots>
          {LOGIN_TIPS.map((t, i) => (
            <Dot key={t.title} $active={i === index} onClick={() => goTo(i)} aria-label={`Show tip: ${t.title}`} />
          ))}
        </Dots>
        <NavButton onClick={() => goTo(index + 1)} aria-label="Next tip">
          <ChevronRight size={18} />
        </NavButton>
      </NavRow>
    </Wrapper>
  );
};

export default LoginTips;
